import { Button, CloseButton, Flex, Input, Spinner } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'

import { FileInput } from '../../../components/FileInput'
import { FormField } from '../../../components/FormField'
import { FormSuccess } from '../../../components/FormSuccess'
import { MoneyInput } from '../../../components/MaskedInput'
import PublicFormLayout from '../../../components/publicFormLayout/PublicFormLayout'
import { FormSelect, SearchableSelect } from '../../../components/Select'
import { useAppToast } from '../../../helpers'
import {
  IConsultantOption,
  ICourseOption,
  IUserOption,
  ONLINE_PAYMENT_METHOD_LABELS,
  OnlinePaymentMethod,
  PAYMENT_METHOD_LABELS,
  PaymentMethod,
  RECEIPT_REFERENCE_LABELS,
  RECEIPT_TYPE_LABELS,
  ReceiptReference,
  ReceiptType,
  SALE_CONDITION_LABELS,
  SALE_PLATFORM_LABELS,
  SaleCondition,
  SalePlatform,
  SalesReceiptService
} from '../../../services/SalesReceipt/SalesReceiptService'

// SalesReceiptController: `receipt => file|mimes:jpeg,jpg,png,pdf|max:5120`.
const MAX_SIZE_MB = 5

// Only presencial card payments carry a Stone ID; online methods never do.
const isCard = (method: string) => method === 'debit' || method === 'credit_machine'

const INITIAL_FORM = {
  commercial_consultant_id: '',
  // Contract extensions are sold by a panel user instead of a consultant (ABRA-388).
  user_id: '',
  registration_number: '',
  receipt_reference: '' as ReceiptReference | '',
  sale_condition: '' as SaleCondition | '',
  payment_method: '' as PaymentMethod | OnlinePaymentMethod | '',
  stone_id: '',
  student_email: '',
  sale_platform: '' as SalePlatform | '',
  value: null as number | null
}

export default function SalesReceiptSubmitPage() {
  const service = useMemo(() => new SalesReceiptService(), [])
  const toast = useAppToast()

  const [receiptType, setReceiptType] = useState<ReceiptType | ''>('')
  const [consultants, setConsultants] = useState<IConsultantOption[]>([])
  // null until loaded: only fetched once the contract extension path is chosen.
  const [users, setUsers] = useState<IUserOption[] | null>(null)
  const [courses, setCourses] = useState<ICourseOption[]>([])
  const [selectedCourseIds, setSelectedCourseIds] = useState<number[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [extracting, setExtracting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const [form, setForm] = useState(INITIAL_FORM)
  // Presencial: uma ou mais matrículas por comprovante (um pagamento pode gerar
  // várias matrículas — ex.: pai que compra cursos para vários filhos).
  const [matriculas, setMatriculas] = useState<string[]>([''])
  const [receipt, setReceipt] = useState<File | null>(null)

  const addMatricula = () => setMatriculas((prev) => [...prev, ''])
  const removeMatricula = (index: number) => setMatriculas((prev) => prev.filter((_, i) => i !== index))
  const updateMatricula = (index: number, value: string) =>
    setMatriculas((prev) => prev.map((m, i) => (i === index ? value.replace(/\D/g, '') : m)))

  const isExtension = receiptType === 'contract_extension'
  const selectedConsultant = isExtension
    ? undefined
    : consultants.find((c) => String(c.id) === form.commercial_consultant_id)
  const selectedUser = isExtension ? users?.find((u) => String(u.id) === form.user_id) : undefined
  // Who sold it: a consultant (enrollment) or a panel user (contract extension).
  const hasSeller = !!(selectedConsultant || selectedUser)
  // "Online (aulas gravadas)" consultants switch the form to the online path (ABRA-162).
  // Contract extensions always use the presencial fields.
  const isOnline = !!selectedConsultant?.is_online_recorded_course

  useEffect(() => {
    service
      .getConsultants()
      .then(setConsultants)
      .catch(() => toast.error('Erro ao carregar consultores'))
  }, [service, toast])

  // Load the user list lazily, only once the contract extension path needs it.
  useEffect(() => {
    if (isExtension && users === null) {
      service
        .getUsers()
        .then(setUsers)
        .catch(() => {
          setUsers([])
          toast.error('Erro ao carregar consultores')
        })
    }
  }, [isExtension, users, service, toast])

  // Load the online course list lazily, only once a consultant needs it.
  useEffect(() => {
    if (isOnline && courses.length === 0) {
      service
        .getCourses()
        .then(setCourses)
        .catch(() => toast.error('Erro ao carregar cursos'))
    }
  }, [isOnline, courses.length, service, toast])

  // Switching type or seller may switch the flow, so clear the path-specific fields to
  // avoid carrying a value from the other path into the submission.
  const resetFields = (seller: Pick<typeof INITIAL_FORM, 'commercial_consultant_id' | 'user_id'>) => {
    setForm({ ...INITIAL_FORM, ...seller, value: form.value })
    setSelectedCourseIds([])
    setMatriculas([''])
  }

  const handleReceiptTypeChange = (type: string) => {
    setReceiptType(type as ReceiptType | '')
    resetFields({ commercial_consultant_id: '', user_id: '' })
  }

  const handleSellerChange = (id: string) =>
    resetFields(
      isExtension ? { commercial_consultant_id: '', user_id: id } : { commercial_consultant_id: id, user_id: '' }
    )

  const handleFileChange = async (file: File | null) => {
    if (!file) {
      setReceipt(null)
      return
    }
    setReceipt(file)

    // Auto-read the receipt with Gemini; the extracted fields stay editable below.
    // Only the value is used on the online path (Stone ID doesn't apply there).
    setExtracting(true)
    try {
      const extraction = await service.extract(file)
      setForm((prev) => ({
        ...prev,
        stone_id: extraction.stone_id ?? prev.stone_id,
        value: extraction.value ?? prev.value
      }))
      toast.info('Comprovante lido! Confira os dados antes de enviar.')
    } catch (e) {
      // Non-blocking: the user can still fill the fields manually.
      toast.warning('Não foi possível ler o comprovante automaticamente')
    } finally {
      setExtracting(false)
    }
  }

  const buildOnlineFormData = (): FormData | null => {
    if (!form.registration_number || !form.student_email || selectedCourseIds.length === 0 || !form.sale_platform) {
      toast.warning('Preencha todos os campos')
      return null
    }
    if (form.sale_platform === 'internal_sale' && !form.payment_method) {
      toast.warning('Informe a forma de pagamento')
      return null
    }
    if (!receipt) {
      toast.warning('Anexe o comprovante')
      return null
    }
    if (!form.value) {
      toast.warning('Informe o valor')
      return null
    }

    const data = new FormData()
    data.append('receipt_type', 'enrollment')
    data.append('commercial_consultant_id', form.commercial_consultant_id)
    // Coluna compartilhada é um array JSON; online envia sempre 1 elemento.
    data.append('registration_number[]', form.registration_number)
    data.append('student_email', form.student_email)
    data.append('sale_platform', form.sale_platform)
    selectedCourseIds.forEach((id) => data.append('course_ids[]', String(id)))
    if (form.sale_platform === 'internal_sale') data.append('payment_method', form.payment_method)
    data.append('value', String(form.value ?? 0))
    data.append('receipt', receipt)
    return data
  }

  const buildPresencialFormData = (): FormData | null => {
    const cleanMatriculas = matriculas.map((m) => m.trim()).filter(Boolean)
    if (cleanMatriculas.length === 0 || !form.receipt_reference || !form.sale_condition || !form.payment_method) {
      toast.warning('Preencha todos os campos')
      return null
    }
    if (!receipt) {
      toast.warning('Anexe o comprovante')
      return null
    }
    if (isCard(form.payment_method) && !form.stone_id) {
      toast.warning('Informe o Stone ID')
      return null
    }
    if (!form.value) {
      toast.warning('Informe o valor')
      return null
    }

    const data = new FormData()
    if (isExtension) {
      data.append('receipt_type', 'contract_extension')
      data.append('user_id', form.user_id)
    } else {
      data.append('receipt_type', 'enrollment')
      data.append('commercial_consultant_id', form.commercial_consultant_id)
    }
    cleanMatriculas.forEach((m) => data.append('registration_number[]', m))
    data.append('receipt_reference', form.receipt_reference)
    data.append('sale_condition', form.sale_condition)
    data.append('payment_method', form.payment_method)
    if (isCard(form.payment_method)) data.append('stone_id', form.stone_id)
    data.append('value', String(form.value ?? 0))
    data.append('receipt', receipt)
    return data
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    // Guard the Enter-key submit path while the receipt is still being read.
    if (extracting) {
      toast.warning('Aguarde a leitura do comprovante')
      return
    }
    if (!receiptType) {
      toast.warning('Selecione o tipo do comprovante')
      return
    }
    if (!hasSeller) {
      toast.warning('Selecione o consultor')
      return
    }

    const data = isOnline ? buildOnlineFormData() : buildPresencialFormData()
    if (!data) return

    setSubmitting(true)
    try {
      await service.submit(data)
      setSubmitted(true)
    } catch (e) {
      toast.error('Erro ao enviar o comprovante', e)
    } finally {
      setSubmitting(false)
    }
  }

  const renderPresencialFields = () => (
    <>
      <FormField label="Nº Matrícula" mb="4" isRequired>
        {matriculas.map((matricula, index) => (
          <Flex key={index} gap="2" mb="2" align="center">
            <Input
              inputMode="numeric"
              value={matricula}
              placeholder={`Matrícula ${index + 1}`}
              onChange={(e) => updateMatricula(index, e.target.value)}
            />
            {matriculas.length > 1 && (
              <CloseButton onClick={() => removeMatricula(index)} aria-label="Remover matrícula" />
            )}
          </Flex>
        ))}
        <Button variant="link" colorScheme="magenta" size="sm" onClick={addMatricula}>
          + Adicionar matrícula
        </Button>
      </FormField>

      <FormField label="Comprovante referente a" mb="4" isRequired>
        <FormSelect
          value={form.receipt_reference}
          onChange={(value) => setForm({ ...form, receipt_reference: value as ReceiptReference })}
          options={Object.entries(RECEIPT_REFERENCE_LABELS).map(([value, label]) => ({
            value: value,
            label: label
          }))}
          placeholder="Selecione"
        />
      </FormField>

      <FormField label="Forma de Pagamento do Contrato" mb="4" isRequired>
        <FormSelect
          value={form.sale_condition}
          onChange={(value) => setForm({ ...form, sale_condition: value as SaleCondition })}
          options={Object.entries(SALE_CONDITION_LABELS).map(([value, label]) => ({
            value: value,
            label: label
          }))}
          placeholder="Selecione"
        />
      </FormField>

      <FormField label="Forma de Pagamento" mb="4" isRequired>
        <FormSelect
          value={form.payment_method}
          onChange={(value) => setForm({ ...form, payment_method: value as PaymentMethod, stone_id: '' })}
          options={Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => ({
            value: value,
            label: label
          }))}
          placeholder="Selecione"
        />
      </FormField>
    </>
  )

  const renderOnlineFields = () => (
    <>
      <FormField label="Nº da Fatura / Pedido" mb="4" isRequired>
        <Input
          value={form.registration_number}
          placeholder="Ex.: F-2026-001"
          onChange={(e) => setForm({ ...form, registration_number: e.target.value })}
        />
      </FormField>

      <FormField label="E-mail do Aluno" mb="4" isRequired>
        <Input
          type="email"
          value={form.student_email}
          onChange={(e) => setForm({ ...form, student_email: e.target.value })}
        />
      </FormField>

      <FormField label="Curso" mb="4" isRequired>
        <SearchableSelect<number>
          isMulti
          placeholder="Selecione um ou mais cursos"
          options={courses.map((c) => ({ value: c.id, label: c.name }))}
          value={selectedCourseIds}
          onChange={setSelectedCourseIds}
          entity="curso"
        />
      </FormField>

      <FormField label="Plataforma da Venda" mb="4" isRequired>
        <FormSelect
          value={form.sale_platform}
          onChange={(value) =>
            // Payment method only applies to Venda Interna; clear it otherwise.
            setForm({ ...form, sale_platform: value as SalePlatform, payment_method: '' })
          }
          options={Object.entries(SALE_PLATFORM_LABELS).map(([value, label]) => ({
            value: value,
            label: label
          }))}
          placeholder="Selecione"
        />
      </FormField>

      {form.sale_platform === 'internal_sale' && (
        <FormField label="Forma de Pagamento" mb="4" isRequired>
          <FormSelect
            value={form.payment_method}
            onChange={(value) => setForm({ ...form, payment_method: value as OnlinePaymentMethod })}
            options={Object.entries(ONLINE_PAYMENT_METHOD_LABELS).map(([value, label]) => ({
              value: value,
              label: label
            }))}
            placeholder="Selecione"
          />
        </FormField>
      )}
    </>
  )

  if (submitted) {
    return (
      <PublicFormLayout title="Comprovante de Venda">
        <FormSuccess title="Comprovante enviado!" description="Recebemos o seu comprovante de venda. Obrigado!" />
      </PublicFormLayout>
    )
  }

  return (
    <PublicFormLayout title="Comprovante de Venda">
      <form onSubmit={handleSubmit}>
        <FormField label="Tipo do Comprovante" mb="4" isRequired>
          <FormSelect
            value={receiptType}
            onChange={handleReceiptTypeChange}
            options={Object.entries(RECEIPT_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
          />
        </FormField>

        {/* The seller list depends on the type: consultants or "Experiência do Aluno" users. */}
        {receiptType && (
          <FormField label="Nome do Consultor" mb="4" isRequired>
            <FormSelect
              value={isExtension ? form.user_id : form.commercial_consultant_id}
              onChange={handleSellerChange}
              options={(isExtension ? (users ?? []) : consultants).map((seller) => ({
                value: seller.id,
                label: seller.name
              }))}
              placeholder="Selecione o consultor"
              isLoading={isExtension && users === null}
            />
          </FormField>
        )}

        {/* Fields depend on the type and the consultant, so wait for a selection first. */}
        {hasSeller && (isOnline ? renderOnlineFields() : renderPresencialFields())}

        {hasSeller && (
          <>
            <FileInput
              accept="receipt"
              maxSizeMB={MAX_SIZE_MB}
              label="Comprovante"
              mb="4"
              isRequired
              preview="thumbnail"
              value={receipt}
              onChange={handleFileChange}
              helperText={
                extracting ? (
                  <Flex align="center" gap="2" color="gray.500">
                    <Spinner size="xs" /> Lendo comprovante...
                  </Flex>
                ) : undefined
              }
            />

            {!isOnline && isCard(form.payment_method) && (
              <FormField label="Stone ID" mb="4" isRequired>
                <Input
                  value={form.stone_id}
                  placeholder="Preenchido automaticamente — confira"
                  onChange={(e) => setForm({ ...form, stone_id: e.target.value })}
                />
              </FormField>
            )}

            <FormField label="Valor" mb="6" isRequired>
              <MoneyInput value={form.value} onChange={(value) => setForm({ ...form, value })} />
            </FormField>

            <Button
              colorScheme="magenta"
              type="submit"
              w="100%"
              size="lg"
              isLoading={submitting}
              isDisabled={extracting}
            >
              Enviar
            </Button>
          </>
        )}
      </form>
    </PublicFormLayout>
  )
}
